import { CANCEL_PLAN_PAYMENT_API, PAST_DUE_PAYMENT_API, REPLACEMENT_DEDUCTIBLE_PAYMENT, getApiUrlForParams } from './../../utils/api.util';
import { Injectable } from '@angular/core';
import { IBillingRequest, IBillingResponse } from '../../interfaces/IJob';
import { HttpService } from '../http/http.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { SAVE_BILLING_INFO } from '../../utils/api.util';
import { DataService } from '../data/data.service';
import { 
  IBankAccountFormGroup,
  IBillingInformationFormGroup,
  ICancelPlanPaymentResponse,
  ICreditCardFormGroup,
  IPastDuePaymentResponse,
  IPayloadForCancelPlanPayment,
  IPayloadForPastDuePayment,
  IPayloadForReplacementDeductible,
  IPaymentType,
  IReplacementDeductibleResponse,
} from '../../interfaces/IPayments';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { 
  ACC_TYPE_NEW,
  BANK_ACC_NO_REGEX,
  BANK_ROUTING_NO_REGEX,
  CARD_NUMBER_REGULAR_REGEX,
  CARD_TYPE_NEW,
  CARD_TYPE_SAVED,
  CVV_REGULAR_REGEX,
  PAYMENT_TYPE_BANK_ACCOUNT,
  PAYMENT_TYPE_CREDIT_CARD,
  PAZE,
  ZIP_CODE_REGEX,
} from '../../utils/constants';
import { expirationDateValidator, monthValidator, routingNumberValidator, yearValidator } from '../../utils/validators';
import { ISubmitClaimReqParamsWithDeductibleForPaze } from '../../components/pages/service-request/service-request.interface';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  termsAndConditionsToDisplay$: BehaviorSubject<IPaymentType|null> = new BehaviorSubject<IPaymentType|null>(null);
  ccForm: FormGroup<ICreditCardFormGroup> = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    cardNumber: new FormControl('', [
      Validators.required,
      Validators.pattern(CARD_NUMBER_REGULAR_REGEX),
      Validators.minLength(15),
    ]),
    expirationDateInput: new FormControl('', [Validators.required, expirationDateValidator.bind(this)]),
    expirationMonth: new FormControl('', [Validators.required, monthValidator]),
    expirationYear: new FormControl('', [Validators.required, yearValidator]),
    cvv: new FormControl('', [Validators.required, Validators.pattern(CVV_REGULAR_REGEX)]),
    typeCode:new FormControl('')
  });
  bankAccForm: FormGroup<IBankAccountFormGroup> = new FormGroup({
    accountNumber: new FormControl('', [Validators.required, Validators.pattern(BANK_ACC_NO_REGEX)]),
    routingNumber: new FormControl('', [Validators.required, Validators.pattern(BANK_ROUTING_NO_REGEX), routingNumberValidator]),
    accountTypes: new FormControl('', [Validators.required])
  });
  billingInfoForm: FormGroup<IBillingInformationFormGroup>= new FormGroup({
    address: new FormControl('', [Validators.required]),
    address2: new FormControl(''),
    city: new FormControl('', [Validators.required]),
    state: new FormControl('', [Validators.required]),
    zipCode: new FormControl('', [Validators.required, Validators.pattern(ZIP_CODE_REGEX)]),
  });

  constructor(private httpService: HttpService, private data: DataService) {}

  // update billing preferences api
  saveBillingInformation( billingRequest: IBillingRequest,  contractNumber: string ): Observable<IBillingResponse>{
    const url = getApiUrlForParams(SAVE_BILLING_INFO,{contractNumber})  ;
    return this.httpService.put<IBillingResponse>(url, billingRequest);
  }

  // cancel plan payment api
  paymentCollectionForCancelPlan(requestPayLoad: IPayloadForCancelPlanPayment): Observable<ICancelPlanPaymentResponse | null> {
    const auth0Id = this.data.auth0UserId.getValue();
    if (!auth0Id) { return of(null); }

    const url = getApiUrlForParams(CANCEL_PLAN_PAYMENT_API, {auth0Id});
    return this.httpService.post<ICancelPlanPaymentResponse>(url, requestPayLoad);
  }

  // past due payment api
  paymentForPastDue(data: IPayloadForPastDuePayment): Observable<IPastDuePaymentResponse | null> {
    const contractNumber = data.contractNumber;
    if (!contractNumber) { return of(null); }

    let url = getApiUrlForParams(`/${PAST_DUE_PAYMENT_API}`, {contractNumber});
    return this.httpService.post<IPastDuePaymentResponse>(url, data);
  }

  // initiate forms
  initiatePaymentOptionsForm(): FormGroup {
    return new FormGroup({
      paymentOption: new FormControl('', Validators.required),
      termsAndConditions: new FormControl('', Validators.required),
    });
  }

  initiateCreditCardForm(): FormGroup {
    return this.ccForm;
  }

  initiateBankAccountForm(): FormGroup {
    return this.bankAccForm;
  }

  initiateBillingInformationForm(): FormGroup {
    return this.billingInfoForm;
  }

  clearForms(): void {
    this.ccForm.reset();
    this.bankAccForm.reset();
    this.billingInfoForm.reset();
  }

  paymentTypeChange(paymentOption: string, paymentTypeSelected: IPaymentType|null): IPaymentType|null {
    switch(paymentOption) {
      case CARD_TYPE_NEW:
        case CARD_TYPE_SAVED:
        paymentTypeSelected = PAYMENT_TYPE_CREDIT_CARD;
        break;
      case ACC_TYPE_NEW:
        paymentTypeSelected = PAYMENT_TYPE_BANK_ACCOUNT;
        break;
      case PAZE:
        paymentTypeSelected = 'PAZE';
        break;
    }
    this.termsAndConditionsToDisplay$.next(paymentTypeSelected);
    return paymentTypeSelected;
  }

  paymentForReplacementDeductible(data: IPayloadForReplacementDeductible|null): Observable<IReplacementDeductibleResponse | null> {
    const auth0Id = this.data.auth0UserId.getValue();
    if (!auth0Id || !data) { return of(null); }

    const contractNumber = data.contractNumber;
    const url = getApiUrlForParams(REPLACEMENT_DEDUCTIBLE_PAYMENT, {auth0Id, contractNumber});
    return this.httpService.post<IReplacementDeductibleResponse>(url, data);
  }
}
